// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { type CSSObject } from '@emotion/react';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import { token } from '@atlaskit/tokens';

import { type Appearance, type Spacing } from '../types';

const gridSize: number = 8;
const HAS_DISABLED_BACKGROUND = ['default', 'primary', 'danger', 'warning'];
const heights: { [key in Spacing]: string } = {
	default: `${32 / 14}em`, // 32px
	compact: `${24 / 14}em`,
	none: 'auto',
};
const lineHeights: { [key in Spacing]: string } = {
	default: heights.default,
	compact: heights.compact,
	none: 'inherit',
};
const padding: { [key in Spacing]: string } = {
	// 10px gutter
	default: `0 ${gridSize + gridSize / 4}px`,
	compact: `0 ${gridSize + gridSize / 4}px`,
	none: '0',
};
const singleIconPadding: { [key in Spacing]: string } = {
	// 2px gutter
	compact: `0 ${gridSize / 4}px`,
	default: `0 ${gridSize / 4}px`,
	none: '0',
};
const verticalAlign: { [key in Spacing]: string } = {
	default: 'middle',
	compact: 'middle',
	none: 'baseline',
};
const defaultAfterStyles: CSSObject = {
	borderRadius: 'inherit',
	inset: token('space.0'),
	borderStyle: 'solid',
	borderWidth: token('border.width'),
	pointerEvents: 'none',
	position: 'absolute',
};
const defaultStyles: CSSObject = {
	background: token('color.background.neutral.subtle'),
	color: token('color.text'),

	'&::after': {
		...defaultAfterStyles,
		content: '""',
		borderColor: token('color.border'),
	},

	'&:hover': {
		background: token('color.background.neutral.hovered'),
	},

	'&:active': {
		background: token('color.background.neutral.pressed'),
	},

	'&[data-has-overlay="true"]:not([disabled]):hover': {
		background: token('color.background.neutral.subtle'),
	},

	'&:disabled[disabled]': {
		background: token('color.background.neutral.subtle'),
	},

	'&:disabled[disabled]:hover': {
		background: token('color.background.neutral.subtle'),
	},

	'&:disabled[disabled]:active': {
		background: token('color.background.neutral.subtle'),
	},
};
const primaryStyles: CSSObject = {
	background: token('color.background.brand.bold'),
	color: token('color.text.inverse'),

	'&:hover': {
		background: token('color.background.brand.bold.hovered'),
	},

	'&:active': {
		background: token('color.background.brand.bold.pressed'),
	},

	'&[data-has-overlay="true"]:not([disabled]):hover': {
		background: token('color.background.brand.bold'),
	},
};
const linkStyles: CSSObject = {
	background: 'transparent',
	color: token('color.link'),

	'&:hover': {
		color: token('color.link'),
		textDecoration: 'underline',
	},

	'&:active': {
		color: token('color.link.pressed'),
		textDecoration: 'underline',
	},
};
const subtleStyles: CSSObject = {
	background: 'transparent',
	color: token('color.text.subtle'),

	'&:hover': {
		background: token('color.background.neutral.subtle.hovered'),
	},

	'&:active': {
		background: token('color.background.neutral.subtle.pressed'),
	},

	'&[data-has-overlay="true"]:not([disabled]):hover': {
		background: 'transparent',
	},
};
const subtleLinkStyles: CSSObject = {
	background: 'transparent',
	color: token('color.text.subtle'),

	'&:hover': {
		background: 'transparent',
		color: token('color.text.subtle'),
		textDecoration: 'underline',
	},

	'&:active': {
		background: 'transparent',
		color: token('color.text'),
		textDecoration: 'underline',
	},
};
const warningStyles: CSSObject = {
	background: token('color.background.warning.bold'),
	color: token('color.text.warning.inverse'),

	'&:hover': {
		background: token('color.background.warning.bold.hovered'),
	},

	'&:active': {
		background: token('color.background.warning.bold.pressed'),
	},

	'&[data-has-overlay="true"]:not([disabled]):hover': {
		background: token('color.background.warning.bold'),
	},
};
const dangerStyles: CSSObject = {
	background: token('color.background.danger.bold'),
	color: token('color.text.inverse'),

	'&:hover': {
		background: token('color.background.danger.bold.hovered'),
	},

	'&:active': {
		background: token('color.background.danger.bold.pressed'),
	},

	'&[data-has-overlay="true"]:not([disabled]):hover': {
		background: token('color.background.danger.bold'),
	},
};
const selectedStyles: CSSObject = {
	background: token('color.background.selected'),
	color: token('color.text.selected'),

	'&:not([disabled])::after': {
		...defaultAfterStyles,
		content: '""',
		borderColor: token('color.border.selected'),
	},
};

/**
 * Anchor-text-color variants applied when the legacy `Button` is rendered as
 * an `<a>` (via `href`). Each `<variant>WithAnchorTextColorStyles` is a
 * self-contained appearance object that adds explicit `color` /
 * `text-decoration` on the base, `:hover`, and `:active` states, preventing
 * browser default or ambient anchor styles from overriding the button's
 * intended text color or adding an unwanted underline.
 *
 * Gated by `platform-dst-legacy-button-anchor-text-color` and used *instead*
 * of the base `<variant>Styles`. When cleaning up the gate: delete the base
 * `<variant>Styles` and rename the `WithAnchorTextColor` variant to replace
 * it.
 *
 * Link-like variants (`link`, `subtle-link`) keep their intentional underline
 * on `:hover` / `:active`.
 */
const defaultWithAnchorTextColorStyles: CSSObject = {
	...defaultStyles,
	textDecoration: 'none',
	'&:hover': {
		...(defaultStyles['&:hover'] as CSSObject),
		color: token('color.text'),
		textDecoration: 'none',
	},
	'&:active': {
		...(defaultStyles['&:active'] as CSSObject),
		color: token('color.text'),
		textDecoration: 'none',
	},
};
const primaryWithAnchorTextColorStyles: CSSObject = {
	...primaryStyles,
	textDecoration: 'none',
	'&:hover': {
		...(primaryStyles['&:hover'] as CSSObject),
		color: token('color.text.inverse'),
		textDecoration: 'none',
	},
	'&:active': {
		...(primaryStyles['&:active'] as CSSObject),
		color: token('color.text.inverse'),
		textDecoration: 'none',
	},
};
const linkWithAnchorTextColorStyles: CSSObject = {
	...linkStyles,
	textDecoration: 'none',
};
const subtleWithAnchorTextColorStyles: CSSObject = {
	...subtleStyles,
	textDecoration: 'none',
	'&:hover': {
		...(subtleStyles['&:hover'] as CSSObject),
		color: token('color.text.subtle'),
		textDecoration: 'none',
	},
	'&:active': {
		...(subtleStyles['&:active'] as CSSObject),
		color: token('color.text.subtle'),
		textDecoration: 'none',
	},
};
const subtleLinkWithAnchorTextColorStyles: CSSObject = {
	...subtleLinkStyles,
	textDecoration: 'none',
};
const warningWithAnchorTextColorStyles: CSSObject = {
	...warningStyles,
	textDecoration: 'none',
	'&:hover': {
		...(warningStyles['&:hover'] as CSSObject),
		color: token('color.text.warning.inverse'),
		textDecoration: 'none',
	},
	'&:active': {
		...(warningStyles['&:active'] as CSSObject),
		color: token('color.text.warning.inverse'),
		textDecoration: 'none',
	},
};
const dangerWithAnchorTextColorStyles: CSSObject = {
	...dangerStyles,
	textDecoration: 'none',
	'&:hover': {
		...(dangerStyles['&:hover'] as CSSObject),
		color: token('color.text.inverse'),
		textDecoration: 'none',
	},
	'&:active': {
		...(dangerStyles['&:active'] as CSSObject),
		color: token('color.text.inverse'),
		textDecoration: 'none',
	},
};
const hasOverlayStyles: CSSObject = {
	'&[data-has-overlay="true"]': {
		cursor: 'default',
		textDecoration: 'none',
	},
};

type GetCssArgs = {
	appearance: Appearance;
	spacing: Spacing;
	isSelected: boolean;
	shouldFitContainer: boolean;
	isOnlySingleIcon: boolean;
};

export function getCss({
	appearance,
	spacing,
	isSelected,
	shouldFitContainer,
	isOnlySingleIcon,
}: GetCssArgs): CSSObject {
	const baseAppearanceStyles = fg('platform-dst-legacy-button-anchor-text-color')
		? {
				...(appearance === 'default' && defaultWithAnchorTextColorStyles),
				...(appearance === 'primary' && primaryWithAnchorTextColorStyles),
				...(appearance === 'link' && linkWithAnchorTextColorStyles),
				...(appearance === 'subtle' && subtleWithAnchorTextColorStyles),
				...(appearance === 'subtle-link' && subtleLinkWithAnchorTextColorStyles),
				...(appearance === 'warning' && warningWithAnchorTextColorStyles),
				...(appearance === 'danger' && dangerWithAnchorTextColorStyles),
			}
		: {
				...(appearance === 'default' && defaultStyles),
				...(appearance === 'primary' && primaryStyles),
				...(appearance === 'link' && linkStyles),
				...(appearance === 'subtle' && subtleStyles),
				...(appearance === 'subtle-link' && subtleLinkStyles),
				...(appearance === 'warning' && warningStyles),
				...(appearance === 'danger' && dangerStyles),
			};
	const appearanceStyles =
		appearance === 'default' && fg('platform-dst-tokens-finesse')
			? {
					...baseAppearanceStyles,
					'&:hover': {
						...(baseAppearanceStyles['&:hover'] as CSSObject),
						background: token('color.background.neutral.subtle.hovered'),
					},
					'&:active': {
						...(baseAppearanceStyles['&:active'] as CSSObject),
						background: token('color.background.neutral.subtle.pressed'),
					},
				}
			: baseAppearanceStyles;
	const activeStyles = appearanceStyles['&:active'] as CSSObject | undefined;

	return {
		// 0px margin added to css-reset
		alignItems: 'baseline',
		borderWidth: 0,
		borderRadius: token('radius.medium'),
		boxSizing: 'border-box',
		display: 'inline-flex',
		fontSize: 'inherit',
		fontStyle: 'normal',
		// Chrome recently changed button so that they use 'arial' as the font family
		fontFamily: 'inherit',
		fontWeight: token('font.weight.medium'),
		// margin for button has been applied to css reset
		maxWidth: '100%',
		// Needed to position overlay
		position: 'relative',
		textAlign: 'center',
		textDecoration: 'none',
		transition: fg('platform-dst-motion-uplift-button')
			? `${token('motion.button.hovered')}, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38)`
			: 'background 0.1s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38)',
		whiteSpace: 'nowrap',
		cursor: 'pointer',
		height: heights[spacing],
		lineHeight: lineHeights[spacing],
		padding: isOnlySingleIcon ? singleIconPadding[spacing] : padding[spacing],
		verticalAlign: verticalAlign[spacing],
		width: shouldFitContainer ? '100%' : 'auto',
		// justifyContent required for shouldFitContainer buttons with an icon inside
		justifyContent: 'center',
		...(isSelected
			? selectedStyles
			: {
					...appearanceStyles,
					...(activeStyles &&
						fg('platform-dst-motion-uplift-button') && {
							'&:active': {
								...activeStyles,
								transition: token('motion.button.pressed'),
							},
						}),

					'&[disabled]': {
						color: token('color.text.disabled'),
						backgroundColor: HAS_DISABLED_BACKGROUND.includes(appearance)
							? token('color.background.disabled')
							: 'transparent',
						cursor: 'not-allowed',
						textDecoration: 'none',

						'&:hovered': {
							backgroundColor: HAS_DISABLED_BACKGROUND.includes(appearance)
								? token('color.background.disabled')
								: 'transparent',
						},

						'&:active': {
							backgroundColor: HAS_DISABLED_BACKGROUND.includes(appearance)
								? token('color.background.disabled')
								: 'transparent',
						},
					},

					...hasOverlayStyles,
				}),
		'&::-moz-focus-inner': {
			border: 0,
			margin: 0,
			padding: 0,
		},
	};
}
